/**
 * @file Album Folder Summary Identity Component.
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import groupBy from 'lodash.groupby';

import IdentityAvatarContainer from '../../containers/global/identity/IdentityAvatarContainer';
import IdentityNameContainer from '../../containers/global/identity/IdentityNameContainer';
import Button from '../global/Button';
import DiscoverAlbumFolderSummaryAlbumFolder from './DiscoverAlbumFolderSummaryAlbumFolder';
import { getIdentityCheckoutPrice } from '../../util/discover-album-folder';
import { PPGD_JOIN_TYPE } from '../../constants/discover-album-folder';
import { formatPriceForDisplay } from '../../util/format-money';
import EditIcon from '../icons/EditIcon';
import TrashIcon from '../icons/TrashIcon';

const propTypes = {
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    selectedAlbums: PropTypes.arrayOf(PropTypes.object).isRequired,
    relationship: PropTypes.string,
  }),
  deselectCustomPrint: PropTypes.func.isRequired,
  onEditSelections: PropTypes.func,
  onRemoveIdentity: PropTypes.func,
  deselectAlbum: PropTypes.func.isRequired,
  hasSearchSubscription: PropTypes.bool,
};

const defaultProps = {
  albumFolder: {
    id: '',
    name: '',
    parent: {
      id: '',
    },
  },
};

const DiscoverAlbumFolderSummaryIdentity = ({
  identity,
  deselectCustomPrint,
  onEditSelections,
  onRemoveIdentity,
  hasSearchSubscription,
  deselectAlbum,
}) => {
  const albumsCount = identity.selectedAlbums.length;

  if (albumsCount === 0) {
    return null;
  }

  const albumFolders = groupBy(identity.selectedAlbums, album => {
    return album.albumFolder.id;
  });
  const selectedAlbumCustomPrintIds = (
    identity?.selectedAlbumCustomPrints ?? []
  ).map(({ id }) => id);
  const price = getIdentityCheckoutPrice(identity);

  return (
    <Fragment>
      <div className="d-flex align-items-center py-1">
        <IdentityAvatarContainer className="mr-2" identity={identity} />

        <div className="d-flex flex-column align-items-start flex-list-item-truncate-30">
          <IdentityNameContainer
            className="text-extra-bold text-dark text-truncate"
            identity={identity}
          />
        </div>
        <div className="ml-auto">&nbsp;</div>
        {onEditSelections && (
          <Button
            btnStyle="reset"
            className="ml-2"
            onClick={() => onEditSelections(identity)}
          >
            <EditIcon color="#0d84fe" size="16px" />
          </Button>
        )}
        {onRemoveIdentity && (
          <Button
            btnStyle="reset"
            className="ml-2"
            onClick={() => onRemoveIdentity(identity)}
          >
            <TrashIcon color="#fe5851" size="14px" />
          </Button>
        )}
      </div>

      <div className="discover-album-folder-summary-expanded-albums">
        <span className="text-sm text-dark text-bold mb-1">
          {albumsCount} {pluralize('Albums', albumsCount)}{' '}
          {hasSearchSubscription && (
            <Fragment>
              {' '}
              &ndash;
              <span className="text-primary ml-1">Included</span>
            </Fragment>
          )}
          {identity.joinType === PPGD_JOIN_TYPE.PURCHASE && price !== 0 && (
            <Fragment>
              {' '}
              &ndash;
              {price !== 0 && (
                <span className="text-dark ml-1">
                  ${formatPriceForDisplay(price)}
                </span>
              )}
            </Fragment>
          )}
        </span>
        {Object.values(albumFolders).map((albumFolderAlbums, index) => (
          <DiscoverAlbumFolderSummaryAlbumFolder
            key={index}
            albumFolderAlbums={albumFolderAlbums}
            selectedAlbumCustomPrintIds={selectedAlbumCustomPrintIds}
            identity={identity}
            deselectCustomPrint={deselectCustomPrint}
            deselectAlbum={deselectAlbum}
          />
        ))}
      </div>
    </Fragment>
  );
};

DiscoverAlbumFolderSummaryIdentity.propTypes = propTypes;
DiscoverAlbumFolderSummaryIdentity.defaultProps = defaultProps;

export default DiscoverAlbumFolderSummaryIdentity;
